<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useQueryClient } from "@tanstack/vue-query";
import { useToast } from "vue-toast-notification";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  assignClientRole,
  getAccessControlCatalog,
  getAccessRoleEffectivePreview,
  getAccessRoles,
  getClientAccessPreview,
  updateClientModuleOverrides,
  updateClientPermissionOverrides,
} from "@/services/accessControlService";
import type { NormalizedClient } from "@/composables/useClientsQuery";
import { useAuthStore } from "@/store/auth";
import type {
  AccessControlCatalogAction,
  AccessControlCatalogModule,
  AccessRole,
  ClientAccessPreview,
} from "@/types/accessControl";

interface ModuleAccessGroup {
  module_key: string;
  label: string | null;
  sidenav_item_id: number | null;
  actions: AccessControlCatalogAction[];
  filtered_actions: AccessControlCatalogAction[];
  is_visible: boolean;
  allowed_count: number;
}

interface ApiErrorLike {
  response?: {
    data?: {
      message?: string;
    };
  };
}

type BooleanStateMap = Record<number, boolean>;

const props = defineProps<{
  open: boolean;
  client: NormalizedClient | null;
}>();

const emit = defineEmits<{
  (e: "update:open", value: boolean): void;
  (e: "saved"): void;
}>();

const queryClient = useQueryClient();
const authStore = useAuthStore();
const toast = useToast();

const accessSearch = ref("");
const isBootstrapping = ref(false);
const isRolePreviewLoading = ref(false);
const isSaving = ref(false);
const workspaceError = ref("");
const availableRoles = ref<AccessRole[]>([]);
const catalogModules = ref<AccessControlCatalogModule[]>([]);
const selectedPreview = ref<ClientAccessPreview | null>(null);
const roleDraft = ref<number | null>(null);
const roleBaseline = ref<number | null>(null);
const moduleDraft = ref<BooleanStateMap>({});
const moduleBaseline = ref<BooleanStateMap>({});
const moduleRoleBase = ref<BooleanStateMap>({});
const permissionDraft = ref<BooleanStateMap>({});
const permissionBaseline = ref<BooleanStateMap>({});
const permissionRoleBase = ref<BooleanStateMap>({});

let rolePreviewRequestId = 0;

// These modules are administrative controls and are not meaningful client
// permissions. They remain available in the internal-user role editor.
const CLIENT_HIDDEN_MODULES = new Set([
  "audit_trail",
  "users",
  "clients",
  "access_control",
]);

function getErrorMessage(error: unknown, fallback: string) {
  const apiError = error as ApiErrorLike;
  return apiError.response?.data?.message || fallback;
}

function normalizeText(value: string | null | undefined) {
  return String(value || "").trim().toLowerCase();
}

const assignableRoles = computed(() => {
  return availableRoles.value.filter((role) => {
    return (
      role.is_active &&
      (role.actor_type === "client" || role.actor_type === "both")
    );
  });
});

const selectedRole = computed(() => {
  if (roleDraft.value == null) {
    return null;
  }

  return assignableRoles.value.find((role) => role.id === roleDraft.value) || null;
});

const roleChanged = computed(() => roleDraft.value !== roleBaseline.value);
const isOverrideEditable = computed(() => !selectedRole.value?.is_superadmin);

const visibleModuleCount = computed(() => {
  return catalogModules.value.filter((module) => {
    if (module.sidenav_item_id === null) {
      return false;
    }

    return Boolean(moduleDraft.value[module.sidenav_item_id]);
  }).length;
});

const allowedPermissionCount = computed(() => {
  return catalogModules.value.reduce((count, module) => {
    return (
      count +
      module.actions.filter((action) => Boolean(permissionDraft.value[action.permission_id]))
        .length
    );
  }, 0);
});

const moduleDirtyCount = computed(() => {
  if (!isOverrideEditable.value) {
    return 0;
  }

  return catalogModules.value.filter((module) => {
    if (module.sidenav_item_id === null) {
      return false;
    }

    return (
      Boolean(moduleDraft.value[module.sidenav_item_id]) !==
      Boolean(moduleBaseline.value[module.sidenav_item_id])
    );
  }).length;
});

const permissionDirtyCount = computed(() => {
  if (!isOverrideEditable.value) {
    return 0;
  }

  return catalogModules.value.reduce((count, module) => {
    return (
      count +
      module.actions.filter((action) => {
        return (
          Boolean(permissionDraft.value[action.permission_id]) !==
          Boolean(permissionBaseline.value[action.permission_id])
        );
      }).length
    );
  }, 0);
});

const totalDirtyCount = computed(() => {
  return Number(roleChanged.value) + moduleDirtyCount.value + permissionDirtyCount.value;
});

const filteredModuleGroups = computed<ModuleAccessGroup[]>(() => {
  const searchValue = normalizeText(accessSearch.value);

  return catalogModules.value
    .map((module) => {
      const filteredActions = module.actions.filter((action) => {
        if (!searchValue) {
          return true;
        }

        return (
          normalizeText(module.label).includes(searchValue) ||
          normalizeText(module.module_key).includes(searchValue) ||
          normalizeText(action.action_key).includes(searchValue) ||
          normalizeText(action.label).includes(searchValue)
        );
      });

      if (
        searchValue &&
        !filteredActions.length &&
        !normalizeText(module.label).includes(searchValue) &&
        !normalizeText(module.module_key).includes(searchValue)
      ) {
        return null;
      }

      return {
        module_key: module.module_key,
        label: module.label,
        sidenav_item_id: module.sidenav_item_id,
        actions: module.actions,
        filtered_actions: filteredActions,
        is_visible:
          module.sidenav_item_id === null
            ? true
            : Boolean(moduleDraft.value[module.sidenav_item_id]),
        allowed_count: module.actions.filter((action) =>
          Boolean(permissionDraft.value[action.permission_id])
        ).length,
      };
    })
    .filter((module): module is ModuleAccessGroup => Boolean(module));
});

function createDefaultModuleState() {
  const state: BooleanStateMap = {};

  catalogModules.value.forEach((module) => {
    if (module.sidenav_item_id !== null) {
      state[module.sidenav_item_id] = false;
    }
  });

  return state;
}

function createDefaultPermissionState() {
  const state: BooleanStateMap = {};

  catalogModules.value.forEach((module) => {
    module.actions.forEach((action) => {
      state[action.permission_id] = false;
    });
  });

  return state;
}

function createModuleStateFromIds(ids: number[]) {
  const state = createDefaultModuleState();

  ids.forEach((id) => {
    state[id] = true;
  });

  return state;
}

function createPermissionStateFromIds(ids: number[]) {
  const state = createDefaultPermissionState();

  ids.forEach((id) => {
    state[id] = true;
  });

  return state;
}

function applyPreviewToDrafts(preview: ClientAccessPreview) {
  selectedPreview.value = preview;
  roleBaseline.value = preview.client.role_id ?? null;
  roleDraft.value = roleBaseline.value;

  moduleBaseline.value = createModuleStateFromIds(
    preview.module_access.effective_visible_module_ids
  );
  moduleDraft.value = { ...moduleBaseline.value };
  moduleRoleBase.value = createModuleStateFromIds(
    preview.module_access.base_visible_module_ids
  );

  permissionBaseline.value = createPermissionStateFromIds(
    preview.permission_access.effective_allowed_permission_ids
  );
  permissionDraft.value = { ...permissionBaseline.value };
  permissionRoleBase.value = createPermissionStateFromIds(
    preview.permission_access.base_allowed_permission_ids
  );
}

function restoreCurrentActorDrafts() {
  if (!selectedPreview.value) {
    return;
  }

  moduleDraft.value = { ...moduleBaseline.value };
  permissionDraft.value = { ...permissionBaseline.value };
  moduleRoleBase.value = createModuleStateFromIds(
    selectedPreview.value.module_access.base_visible_module_ids
  );
  permissionRoleBase.value = createPermissionStateFromIds(
    selectedPreview.value.permission_access.base_allowed_permission_ids
  );
}

function resetDraftsToEmptyRole() {
  const nextModuleState = createDefaultModuleState();
  const nextPermissionState = createDefaultPermissionState();

  moduleRoleBase.value = { ...nextModuleState };
  permissionRoleBase.value = { ...nextPermissionState };
  moduleDraft.value = { ...nextModuleState };
  permissionDraft.value = { ...nextPermissionState };
}

async function syncRoleDrivenDrafts(roleId: number | null) {
  if (!selectedPreview.value) {
    return;
  }

  if (roleId === roleBaseline.value) {
    restoreCurrentActorDrafts();
    return;
  }

  if (roleId === null) {
    resetDraftsToEmptyRole();
    return;
  }

  const requestId = ++rolePreviewRequestId;
  isRolePreviewLoading.value = true;

  try {
    const preview = await getAccessRoleEffectivePreview(roleId);
    if (requestId !== rolePreviewRequestId) {
      return;
    }

    const nextModuleState = createModuleStateFromIds(
      preview.module_access.effective_visible_module_ids
    );
    const nextPermissionState = createPermissionStateFromIds(
      preview.permission_access.effective_allowed_permission_ids
    );

    moduleRoleBase.value = { ...nextModuleState };
    permissionRoleBase.value = { ...nextPermissionState };
    moduleDraft.value = { ...nextModuleState };
    permissionDraft.value = { ...nextPermissionState };
    workspaceError.value = "";
  } catch (error) {
    workspaceError.value = getErrorMessage(
      error,
      "Unable to load the selected role defaults right now."
    );
  } finally {
    if (requestId === rolePreviewRequestId) {
      isRolePreviewLoading.value = false;
    }
  }
}

function setModuleVisibility(
  sidenavItemId: number,
  actions: AccessControlCatalogAction[],
  value: boolean
) {
  moduleDraft.value = {
    ...moduleDraft.value,
    [sidenavItemId]: value,
  };

  const nextPermissionDraft = { ...permissionDraft.value };
  if (!value) {
    actions.forEach((action) => {
      nextPermissionDraft[action.permission_id] = false;
    });
  }
  permissionDraft.value = nextPermissionDraft;
}

function setPermissionAllowance(permissionId: number, value: boolean) {
  permissionDraft.value = {
    ...permissionDraft.value,
    [permissionId]: value,
  };
}

async function bootstrapWorkspace(clientId: number | string) {
  isBootstrapping.value = true;
  workspaceError.value = "";
  accessSearch.value = "";

  try {
    const [catalogResponse, rolesResponse, preview] = await Promise.all([
      getAccessControlCatalog(),
      getAccessRoles({ actor_type: "client", is_active: true }),
      getClientAccessPreview(clientId),
    ]);

    catalogModules.value = catalogResponse.modules.filter(
      (module) => !CLIENT_HIDDEN_MODULES.has(module.module_key)
    );
    availableRoles.value = Array.isArray(rolesResponse.roles) ? rolesResponse.roles : [];
    applyPreviewToDrafts(preview);
  } catch (error) {
    workspaceError.value = getErrorMessage(
      error,
      "Unable to load the client access configuration right now."
    );
  } finally {
    isBootstrapping.value = false;
  }
}

watch(
  [() => props.client?.id, () => props.open],
  ([clientId, open]) => {
    if (!clientId || !open || !props.client) {
      return;
    }

    rolePreviewRequestId = 0;
    selectedPreview.value = null;
    availableRoles.value = [];
    catalogModules.value = [];
    roleDraft.value = null;
    roleBaseline.value = null;
    moduleDraft.value = {};
    moduleBaseline.value = {};
    moduleRoleBase.value = {};
    permissionDraft.value = {};
    permissionBaseline.value = {};
    permissionRoleBase.value = {};
    workspaceError.value = "";
    accessSearch.value = "";
    bootstrapWorkspace(props.client.raw?.id || props.client.id);
  },
  { immediate: true }
);

watch(roleDraft, (roleId) => {
  if (isBootstrapping.value || !selectedPreview.value) {
    return;
  }

  workspaceError.value = "";
  syncRoleDrivenDrafts(roleId);
});

async function saveChanges() {
  if (!props.client || !selectedPreview.value || totalDirtyCount.value === 0) {
    return;
  }

  isSaving.value = true;

  try {
    const clientId = props.client.raw?.id || props.client.id;

    if (roleChanged.value) {
      await assignClientRole(clientId, {
        role_id: roleDraft.value,
      });
    }

    if (moduleDirtyCount.value > 0) {
      await updateClientModuleOverrides(
        clientId,
        catalogModules.value
          .filter((module) => module.sidenav_item_id !== null)
          .filter((module) => {
            const sidenavItemId = module.sidenav_item_id as number;
            return (
              Boolean(moduleDraft.value[sidenavItemId]) !==
              Boolean(moduleRoleBase.value[sidenavItemId])
            );
          })
          .map((module) => ({
            sidenav_item_id: module.sidenav_item_id as number,
            is_visible: Boolean(moduleDraft.value[module.sidenav_item_id as number]),
          }))
      );
    }

    if (permissionDirtyCount.value > 0) {
      await updateClientPermissionOverrides(
        clientId,
        catalogModules.value.flatMap((module) =>
          module.actions
            .filter((action) => {
              return (
                Boolean(permissionDraft.value[action.permission_id]) !==
                Boolean(permissionRoleBase.value[action.permission_id])
              );
            })
            .map((action) => ({
              permission_id: action.permission_id,
              is_allow: Boolean(permissionDraft.value[action.permission_id]),
            }))
        )
      );
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["clients"] }),
      queryClient.invalidateQueries({ queryKey: ["sidenav"] }),
    ]);

    if (
      authStore.accountType === "client" &&
      authStore.clientProfile?.id === Number(clientId)
    ) {
      await authStore.restoreSession();
      await queryClient.invalidateQueries({ queryKey: ["sidenav"] });
    }

    toast.success("Client access settings updated.");
    emit("saved");
    emit("update:open", false);
  } catch (error) {
    toast.error(getErrorMessage(error, "Unable to save client access settings."));
  } finally {
    isSaving.value = false;
  }
}

function clientInitials() {
  const name = props.client?.companyName || props.client?.company_name || props.client?.raw?.company_name || "CL";
  const parts = String(name).split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((part) => part.charAt(0).toUpperCase()).join("") || "CL";
}

function moduleDisplayLabel(module: ModuleAccessGroup) {
  return module.module_key === "social_media"
    ? "Social Media"
    : module.label || module.module_key;
}
</script>

<template>
  <Dialog
    :open="open"
    @update:open="$emit('update:open', $event)"
  >
    <DialogContent
      class="max-w-6xl max-h-[92vh] rounded-[1.25rem] p-0 gap-0 border-0 bg-white shadow-[0_32px_80px_rgba(0,0,0,0.18)] overflow-hidden"
    >
      <div class="flex flex-col max-h-[92vh]">
        <DialogHeader
          class="flex items-left justify-between gap-4 px-7 py-6 border-b border-[rgba(224,230,240,1)] bg-white rounded-t-[1.25rem] shrink-0"
        >
          <div>
            <DialogTitle class="m-0 text-[1.35rem] font-extrabold text-[#172033]">
              Configure Client Access
            </DialogTitle>
            <p class="mt-2 text-sm text-[#64748b]">
              Manage this client’s assigned role, module visibility, and direct action overrides.
            </p>
          </div>
        </DialogHeader>

        <div class="flex-1 overflow-y-auto px-7 py-6">
          <div
            v-if="client"
            class="mb-6 grid gap-4 rounded-[24px] border border-slate-200/80 bg-slate-50/80 p-5 md:grid-cols-[auto_minmax(0,1fr)_auto]"
          >
            <div
              class="flex h-14 w-14 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#e9f1ff_0%,#bed4ff_100%)] text-lg font-extrabold text-[#22427f]"
            >
              {{ clientInitials() }}
            </div>

            <div class="min-w-0">
              <h3 class="truncate text-lg font-bold text-slate-900">
                {{ client.companyName || client.company_name || "Unknown Client" }}
              </h3>
              <p class="mt-1 truncate text-sm text-slate-500">
                {{ client.email || "No email available" }}
              </p>
              <div class="mt-3 flex flex-wrap items-center gap-2">
                <span class="rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                  {{ client.clientAccountStatus || client.client_account_status || "-" }}
                </span>
              </div>
            </div>

            <div class="grid gap-2 text-sm text-slate-500 md:justify-items-end">
              <div>
                <span class="font-semibold text-slate-700">Client ID:</span>
                {{ client.referenceCode || client.client_id || "-" }}
              </div>
              <div>
                <span class="font-semibold text-slate-700">Plan:</span>
                {{ client.planLabel || "-" }}
              </div>
              <div>
                <span class="font-semibold text-slate-700">Status:</span>
                {{ client.clientAccountStatus || client.client_account_status || "-" }}
              </div>
            </div>
          </div>

          <section class="rounded-[1.1rem] border border-[rgba(219,227,239,0.9)] bg-white p-5 shadow-[0_16px_36px_rgba(15,23,42,0.05)]">
            <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p class="text-[0.98rem] font-extrabold text-[#172033]">
                  Client Access Overrides
                </p>
                <p class="mt-1 text-[0.8rem] text-[#64748b]">
                  Assign a client role, then adjust module visibility and action permissions only where this client needs exceptions.
                </p>
              </div>

              <div class="w-full max-w-md">
                <label class="mb-2 block text-[11px] font-extrabold uppercase tracking-[0.12em] text-slate-500">
                  Search Module Or Permission
                </label>
                <Input
                  v-model="accessSearch"
                  class="h-11 rounded-2xl border-[rgba(211,220,233,1)] bg-white"
                  placeholder="Search module or action"
                />
              </div>
            </div>

            <div class="mt-5 grid gap-5 xl:grid-cols-[300px_minmax(0,1fr)]">
              <div class="space-y-4">
                <div class="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">
                  <article class="rounded-[1rem] border border-[rgba(219,227,239,0.9)] bg-[#f8fbff] px-4 py-4">
                    <span class="text-[11px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
                      Visible Modules
                    </span>
                    <strong class="mt-2 block text-2xl font-extrabold text-slate-900">
                      {{ visibleModuleCount }}
                    </strong>
                  </article>

                  <article class="rounded-[1rem] border border-[rgba(219,227,239,0.9)] bg-[#f8fbff] px-4 py-4">
                    <span class="text-[11px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
                      Allowed Actions
                    </span>
                    <strong class="mt-2 block text-2xl font-extrabold text-slate-900">
                      {{ allowedPermissionCount }}
                    </strong>
                  </article>

                  <article class="rounded-[1rem] border border-[rgba(219,227,239,0.9)] bg-[#f8fbff] px-4 py-4">
                    <span class="text-[11px] font-extrabold uppercase tracking-[0.08em] text-slate-500">
                      Unsaved Changes
                    </span>
                    <strong class="mt-2 block text-2xl font-extrabold text-slate-900">
                      {{ totalDirtyCount }}
                    </strong>
                  </article>
                </div>
              </div>

              <div class="space-y-4">
                <div
                  v-if="isBootstrapping || isRolePreviewLoading"
                  class="flex items-center justify-center rounded-[1rem] border border-[rgba(219,227,239,0.9)] bg-[#f8fbff] px-6 py-16"
                >
                  <div class="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-500" />
                </div>

                <div
                  v-else-if="workspaceError"
                  class="rounded-[1rem] border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700"
                >
                  {{ workspaceError }}
                </div>

                <div
                  v-else-if="!filteredModuleGroups.length"
                  class="rounded-[1rem] border border-dashed border-slate-300 bg-slate-50 px-6 py-12 text-center text-sm text-slate-500"
                >
                  No modules or permissions matched your search.
                </div>

                <div
                  v-else
                  class="space-y-4"
                >
                  <div
                    v-for="module in filteredModuleGroups"
                    :key="module.module_key"
                    class="overflow-hidden rounded-[1rem] border border-[rgba(219,227,239,0.9)] bg-white"
                  >
                    <div class="flex flex-col gap-4 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                      <div class="min-w-0">
                        <div class="flex flex-wrap items-center gap-2">
                          <h3 class="text-base font-extrabold text-slate-900">
                            {{ moduleDisplayLabel(module) }}
                          </h3>
                          <span
                            class="rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.08em]"
                            :class="
                              module.is_visible
                                ? 'border border-blue-200 bg-blue-50 text-blue-700'
                                : 'border border-slate-200 bg-slate-100 text-slate-600'
                            "
                          >
                            {{ module.is_visible ? "visible" : "hidden" }}
                          </span>
                        </div>
                        <p class="mt-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                          {{ module.allowed_count }} of {{ module.actions.length }} actions allowed
                        </p>
                      </div>

                      <div class="flex flex-wrap items-center gap-3">
                        <label
                          v-if="module.sidenav_item_id !== null"
                          class="inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm"
                          :class="{
                            'cursor-pointer': isOverrideEditable,
                            'cursor-not-allowed opacity-60': !isOverrideEditable,
                          }"
                        >
                          <button
                            type="button"
                            role="switch"
                            :aria-checked="module.is_visible"
                            :aria-label="`${module.is_visible ? 'Disable' : 'Enable'} sidebar visibility`"
                            :disabled="!isOverrideEditable || isSaving"
                            class="relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-all duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed"
                            :class="module.is_visible ? 'bg-blue-600' : 'bg-slate-300'"
                            @click="
                              setModuleVisibility(
                                module.sidenav_item_id as number,
                                module.actions,
                                !module.is_visible
                              )
                            "
                          >
                            <span
                              class="inline-block h-5 w-5 rounded-full bg-white shadow-md transition-transform duration-200 ease-in-out"
                              :class="module.is_visible ? 'translate-x-6' : 'translate-x-1'"
                            />
                          </button>

                          <span
                            class="text-sm font-semibold"
                            :class="module.is_visible ? 'text-blue-600' : 'text-slate-500'"
                          >
                            {{ module.is_visible ? "Show" : "Hide" }}
                          </span>
                        </label>

                      </div>
                    </div>

                    <div
                      v-if="module.is_visible || module.sidenav_item_id === null"
                      class="border-t border-[rgba(232,237,245,1)] bg-[#fbfcff] px-5 py-4"
                    >
                      <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                        <label
                          v-for="action in module.filtered_actions"
                          :key="action.permission_id"
                          class="flex items-center gap-3 rounded-[0.95rem] border border-[rgba(219,227,239,0.9)] bg-white px-4 py-3"
                          :class="{
                            'cursor-pointer': isOverrideEditable,
                            'cursor-not-allowed opacity-60': !isOverrideEditable || !module.is_visible,
                          }"
                        >
                          <input
                            :checked="Boolean(permissionDraft[action.permission_id])"
                            type="checkbox"
                            class="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            :disabled="!isOverrideEditable || !module.is_visible || isSaving"
                            @change="
                              setPermissionAllowance(
                                action.permission_id,
                                ($event.target as HTMLInputElement).checked
                              )
                            "
                          >
                          <span class="text-sm font-medium text-slate-700">
                            {{ action.label || action.action_key }}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <DialogFooter
          class="flex justify-end gap-[0.75rem] px-7 py-6 border-t border-[rgba(224,230,240,1)] bg-white rounded-b-[1.25rem] shrink-0"
        >
          <Button
            type="button"
            variant="outline"
            class="min-h-[2.5rem] px-[1.15rem] py-[0.7rem] rounded-[0.95rem] text-[0.88rem] font-bold border-[rgba(49,125,255,0.22)] bg-[#f6f9ff] text-[#2855a6] hover:bg-[#ebf2ff]"
            @click="$emit('update:open', false)"
          >
            Cancel
          </Button>
          <Button
            type="button"
            class="min-h-[2.5rem] px-[1.15rem] py-[0.7rem] rounded-[0.95rem] text-[0.88rem] font-bold bg-[#1f7aff] text-white hover:bg-[#1f7aff]"
            :disabled="isSaving || isBootstrapping || isRolePreviewLoading || totalDirtyCount === 0"
            @click="saveChanges"
          >
            {{ isSaving ? "Saving..." : `Save Changes${totalDirtyCount ? ` (${totalDirtyCount})` : ""}` }}
          </Button>
        </DialogFooter>
      </div>
    </DialogContent>
  </Dialog>
</template>
